// ─────────────────────────────────────────────────────────────────────────────
// PasswordStrength — the strength meter + live requirements checklist shown under
// a new-password field. The checklist rules mirror validatePasswordPolicy (and
// the backend's password-policy.ts) so the user sees exactly what's still missing
// before they submit. Purely presentational: give it a password, it renders.
// ─────────────────────────────────────────────────────────────────────────────
import { getPasswordStrength } from "../utils/passwordStrength";
import styles from "../Auth.module.css";

const EMPTY_SEGMENT = "#1e2433";

// Same four rules the policy validator enforces, in the order shown to the user.
function requirementsFor(password) {
  return [
    { label: "At least 8 characters",     ok: password.length >= 8 },
    { label: "An uppercase letter (A–Z)", ok: /[A-Z]/.test(password) },
    { label: "A lowercase letter (a–z)",  ok: /[a-z]/.test(password) },
    { label: "A number (0–9)",            ok: /[0-9]/.test(password) },
  ];
}

export function PasswordStrength({ password }) {
  const strength = getPasswordStrength(password);
  const rules = requirementsFor(password);

  return (
    <>
      {/* Meter — 4 segments filled up to the score. Colours are data-driven, so
          they stay inline; layout lives in the CSS module. */}
      {password && (
        <div className={styles.strengthWrap}>
          <div className={styles.strengthBar}>
            {[1, 2, 3, 4].map((seg) => (
              <span
                key={seg}
                className={styles.strengthSeg}
                style={{ background: seg <= strength.score ? strength.color : EMPTY_SEGMENT }}
              />
            ))}
          </div>
          <span className={styles.strengthLabel} style={{ color: strength.color }}>
            {strength.label}
          </span>
        </div>
      )}

      {/* Checklist — each rule turns green with a ✓ once met. */}
      <ul className={styles.reqList}>
        <li className={styles.reqHint}>Your password must contain:</li>
        {rules.map((r) => (
          <li key={r.label} className={`${styles.reqItem} ${r.ok ? styles.reqItemOk : ""}`}>
            <span className={styles.reqIcon}>{r.ok ? "✓" : "○"}</span>
            {r.label}
          </li>
        ))}
      </ul>
    </>
  );
}

export default PasswordStrength;
